import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Badge } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from "chart.js";
import api, { parseWIB } from "../services/api";
import Pagination from "../components/Pagination";
import {
  DocumentDownload,
  Calendar,
  Chart,
  Money4,
  Tag,
  Box,
  TrendUp,
  ArrowDown,
  ArrowUp,
} from "iconsax-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
);

const Reports = () => {
  const [salesReport, setSalesReport] = useState([]);
  const [profitReport, setProfitReport] = useState([]);
  const [stockReport, setStockReport] = useState([]);
  const [filter, setFilter] = useState({
    date_from: "",
    date_to: "",
    group_by: "day",
  });
  const [activeTab, setActiveTab] = useState("sales");
  const [loading, setLoading] = useState(false);
  // Sales & profit dipaginasi di sisi klien supaya grafik dan baris TOTAL
  // tetap dihitung dari seluruh periode, bukan cuma halaman aktif.
  const [salesPage, setSalesPage] = useState(1);
  const [profitPage, setProfitPage] = useState(1);
  // Stok dipaginasi di server (jumlah barisnya tak terbatas)
  const [stockPage, setStockPage] = useState(1);
  const [stockTotal, setStockTotal] = useState(0);
  const PAGE_SIZE = 20;

  const applyPreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    const todayStr = fmt(today);
    if (preset === "today") {
      setFilter((f) => ({ ...f, date_from: todayStr, date_to: todayStr }));
    } else if (preset === "7d") {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      setFilter((f) => ({ ...f, date_from: fmt(d), date_to: todayStr }));
    } else if (preset === "30d") {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      setFilter((f) => ({ ...f, date_from: fmt(d), date_to: todayStr }));
    } else if (preset === "month") {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      setFilter((f) => ({
        ...f,
        date_from: fmt(d),
        date_to: todayStr,
        group_by: "day",
      }));
    } else if (preset === "all") {
      setFilter((f) => ({ ...f, date_from: "", date_to: "" }));
    }
  };

  const loadReports = useCallback(
    async (overrideFilter, stockTargetPage) => {
      setLoading(true);
      try {
        const params = { ...(overrideFilter || filter) };
        if (activeTab === "sales") {
          const res = await api.get("/reports/sales", { params });
          setSalesReport(res.data);
          setSalesPage(1);
        } else if (activeTab === "profit") {
          const res = await api.get("/reports/profit", { params });
          setProfitReport(res.data);
          setProfitPage(1);
        } else if (activeTab === "stock") {
          const targetPage = stockTargetPage || 1;
          const res = await api.get("/reports/stock", {
            params: { ...params, page: targetPage, limit: PAGE_SIZE },
          });
          const data = res.data;
          // Backend lama bisa mengembalikan array polos
          if (Array.isArray(data)) {
            setStockReport(data);
            setStockTotal(data.length);
            setStockPage(1);
          } else {
            setStockReport(data.movements || []);
            setStockTotal(data.total || 0);
            setStockPage(data.page || targetPage);
          }
        }
      } catch (error) {
        toast.error("Gagal memuat laporan");
      } finally {
        setLoading(false);
      }
    },
    [activeTab, filter],
  );

  useEffect(() => {
    loadReports();
  }, [activeTab]); // eslint-disable-line

  const handleStockPageChange = (newPage) => {
    const maxPage = Math.max(Math.ceil(stockTotal / PAGE_SIZE), 1);
    if (newPage < 1 || newPage > maxPage || newPage === stockPage) return;
    loadReports(null, newPage);
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.date_from) params.append("date_from", filter.date_from);
      if (filter.date_to) params.append("date_to", filter.date_to);
      const res = await api.get(`/reports/export/excel?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `laporan-${filter.date_from || "all"}-${filter.date_to || "all"}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export berhasil");
    } catch (error) {
      toast.error("Gagal export Excel");
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
  const fmtDt = (d) => {
    if (!d) return "-";
    const dt = parseWIB(d);
    return (
      dt.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      ", " +
      dt.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  };

  const totalSales = salesReport.reduce(
    (sum, r) => sum + Number(r.total_sales),
    0,
  );
  const totalServiceSales = salesReport.reduce(
    (sum, r) => sum + Number(r.service_sales),
    0,
  );
  const totalProductSales = salesReport.reduce(
    (sum, r) => sum + Number(r.product_sales),
    0,
  );
  const totalTransactions = salesReport.reduce(
    (sum, r) => sum + Number(r.total_transactions),
    0,
  );

  // Baris yang tampil di halaman aktif (totals di atas tetap pakai data penuh)
  const salesPageRows = salesReport.slice(
    (salesPage - 1) * PAGE_SIZE,
    salesPage * PAGE_SIZE,
  );
  const profitPageRows = profitReport.slice(
    (profitPage - 1) * PAGE_SIZE,
    profitPage * PAGE_SIZE,
  );

  const PRESETS = [
    { key: "today", label: "Hari Ini" },
    { key: "7d", label: "7 Hari" },
    { key: "30d", label: "30 Hari" },
    { key: "month", label: "Bulan Ini" },
    { key: "all", label: "Semua" },
  ];

  const TABS = [
    { key: "sales", label: "Penjualan", icon: <Chart size={15} /> },
    { key: "profit", label: "Laba Rugi", icon: <TrendUp size={15} /> },
    { key: "stock", label: "Stok Keluar", icon: <Box size={15} /> },
  ];

  const chartData = {
    labels: salesReport.map((r) => r.period),
    datasets: [
      {
        label: "Total",
        data: salesReport.map((r) => r.total_sales),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59,130,246,0.08)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Layanan",
        data: salesReport.map((r) => r.service_sales),
        borderColor: "#10b981",
        tension: 0.4,
      },
      {
        label: "Produk",
        data: salesReport.map((r) => r.product_sales),
        borderColor: "#f59e0b",
        tension: 0.4,
      },
    ],
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-2">
          <Chart size={22} color="#3b82f6" variant="Bold" />
          <h5 className="fw-bold mb-0">Laporan</h5>
        </div>
        <button
          className="btn btn-success d-flex align-items-center gap-2"
          onClick={exportExcel}
        >
          <DocumentDownload size={18} />
          <span className="d-none d-sm-inline">Export Excel</span>
        </button>
      </div>

      {/* Filter Card */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          {/* Preset Buttons */}
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <Calendar size={16} color="#6b7280" />
            <span className="text-muted small me-1">Periode:</span>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                className="btn btn-sm btn-outline-primary"
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Manual Inputs */}
          <div className="row g-2 align-items-end">
            <div className="col-sm-4 col-md-3">
              <label className="form-label small mb-1">Dari Tanggal</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.date_from}
                onChange={(e) =>
                  setFilter({ ...filter, date_from: e.target.value })
                }
              />
            </div>
            <div className="col-sm-4 col-md-3">
              <label className="form-label small mb-1">Sampai Tanggal</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filter.date_to}
                onChange={(e) =>
                  setFilter({ ...filter, date_to: e.target.value })
                }
              />
            </div>
            <div className="col-sm-4 col-md-3">
              <label className="form-label small mb-1">Kelompokkan</label>
              <select
                className="form-select form-select-sm"
                value={filter.group_by}
                onChange={(e) =>
                  setFilter({ ...filter, group_by: e.target.value })
                }
              >
                <option value="day">Harian</option>
                <option value="month">Bulanan</option>
                <option value="year">Tahunan</option>
              </select>
            </div>
            <div className="col-sm-12 col-md-3">
              <button
                className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
                onClick={() => loadReports()}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />{" "}
                    Memuat...
                  </>
                ) : (
                  <>
                    <Chart size={15} /> Tampilkan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Strip â€” only for sales tab */}
      {activeTab === "sales" && (
        <div className="row g-2 mb-3">
          {[
            {
              label: "Total Penjualan",
              value: fmt(totalSales),
              color: "#3b82f6",
              icon: <Money4 size={18} color="#3b82f6" variant="Bold" />,
            },
            {
              label: "Layanan",
              value: fmt(totalServiceSales),
              color: "#10b981",
              icon: <Tag size={18} color="#10b981" variant="Bold" />,
            },
            {
              label: "Produk",
              value: fmt(totalProductSales),
              color: "#f59e0b",
              icon: <Box size={18} color="#f59e0b" variant="Bold" />,
            },
            {
              label: "Transaksi",
              value: totalTransactions,
              color: "#8b5cf6",
              icon: <Chart size={18} color="#8b5cf6" variant="Bold" />,
            },
          ].map((s, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div
                      className="rounded p-1"
                      style={{ background: s.color + "18" }}
                    >
                      {s.icon}
                    </div>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {s.label}
                    </span>
                  </div>
                  <div className="fw-bold" style={{ fontSize: "1rem" }}>
                    {s.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="d-flex gap-2 mb-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm d-flex align-items-center gap-1 ${
              activeTab === t.key ? "btn-primary" : "btn-outline-secondary"
            }`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-2" />
          <div className="text-muted small">Memuat data laporan...</div>
        </div>
      ) : (
        <>
          {/* SALES */}
          {activeTab === "sales" && (
            <div>
              {salesReport.length > 0 && (
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: { boxWidth: 12, font: { size: 11 } },
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              callback: (v) =>
                                "Rp " + Number(v).toLocaleString("id-ID"),
                              font: { size: 10 },
                            },
                            grid: { color: "rgba(0,0,0,0.05)" },
                          },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Periode</th>
                        <th className="text-center">Transaksi</th>
                        <th className="text-end">Layanan</th>
                        <th className="text-end">Produk</th>
                        <th className="text-end">Diskon</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesReport.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="text-center text-muted py-4"
                          >
                            Tidak ada data untuk filter ini
                          </td>
                        </tr>
                      ) : (
                        salesPageRows.map((r) => (
                          <tr key={r.period}>
                            <td className="fw-semibold">{r.period}</td>
                            <td className="text-center">
                              <span className="badge bg-light text-dark">
                                {r.total_transactions}
                              </span>
                            </td>
                            <td className="text-end">{fmt(r.service_sales)}</td>
                            <td className="text-end">{fmt(r.product_sales)}</td>
                            <td className="text-end text-danger">
                              -{fmt(r.total_discount)}
                            </td>
                            <td className="text-end fw-bold text-primary">
                              {fmt(r.total_sales)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {salesReport.length > 0 && (
                      <tfoot className="table-light fw-bold">
                        <tr>
                          <td>TOTAL</td>
                          <td className="text-center">{totalTransactions}</td>
                          <td className="text-end">{fmt(totalServiceSales)}</td>
                          <td className="text-end">{fmt(totalProductSales)}</td>
                          <td className="text-end text-danger">
                            -
                            {fmt(
                              salesReport.reduce(
                                (s, r) => s + Number(r.total_discount),
                                0,
                              ),
                            )}
                          </td>
                          <td className="text-end text-primary">
                            {fmt(totalSales)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
              <Pagination
                page={salesPage}
                totalCount={salesReport.length}
                pageSize={PAGE_SIZE}
                onPageChange={setSalesPage}
                label="periode"
              />
            </div>
          )}

          {/* PROFIT */}
          {activeTab === "profit" && (
            <div>
              <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Item</th>
                        <th>Tipe</th>
                        <th className="text-center">Qty</th>
                        <th className="text-end">Revenue</th>
                        <th className="text-end">Modal</th>
                        <th className="text-end">Laba</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitReport.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="text-center text-muted py-4"
                          >
                            Tidak ada data untuk filter ini
                          </td>
                        </tr>
                      ) : (
                        profitPageRows.map((r) => (
                          <tr key={`${r.item_type}-${r.item_name}`}>
                            <td>{r.item_name}</td>
                            <td>
                              <Badge
                                bg={
                                  r.item_type === "service" ? "info" : "success"
                                }
                                className="fw-normal"
                              >
                                {r.item_type === "service" ? "Jasa" : "Produk"}
                              </Badge>
                            </td>
                            <td className="text-center">{r.total_qty}</td>
                            <td className="text-end">{fmt(r.total_revenue)}</td>
                            <td className="text-end text-muted">
                              {fmt(r.total_cost)}
                            </td>
                            <td className="text-end fw-bold text-success">
                              {fmt(r.profit)}
                            </td>
                          </tr>
                        ))
                      )}
                      {profitReport.length > 0 && (
                        <tr className="table-light fw-bold">
                          <td colSpan="3">TOTAL</td>
                          <td className="text-end">
                            {fmt(
                              profitReport.reduce(
                                (s, r) => s + Number(r.total_revenue),
                                0,
                              ),
                            )}
                          </td>
                          <td className="text-end text-muted">
                            {fmt(
                              profitReport.reduce(
                                (s, r) => s + Number(r.total_cost),
                                0,
                              ),
                            )}
                          </td>
                          <td className="text-end text-success">
                            {fmt(
                              profitReport.reduce(
                                (s, r) => s + Number(r.profit),
                                0,
                              ),
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                page={profitPage}
                totalCount={profitReport.length}
                pageSize={PAGE_SIZE}
                onPageChange={setProfitPage}
                label="item"
              />
            </div>
          )}

          {/* STOCK */}
          {activeTab === "stock" && (
            <div>
              <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tanggal</th>
                        <th>Produk</th>
                        <th>Tipe</th>
                        <th className="text-center">Qty</th>
                        <th>Referensi</th>
                        <th>Catatan</th>
                        <th>Oleh</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockReport.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center text-muted py-4"
                          >
                            Tidak ada data untuk filter ini
                          </td>
                        </tr>
                      ) : (
                        stockReport.map((r) => (
                          <tr key={r.id}>
                            <td className="text-nowrap">
                              {fmtDt(r.created_at)}
                            </td>
                            <td>
                              {r.product_name}{" "}
                              <small className="text-muted">({r.sku})</small>
                            </td>
                            <td>
                              <Badge
                                bg={r.type === "in" ? "success" : "danger"}
                                className="fw-normal d-flex align-items-center gap-1"
                                style={{ width: "fit-content" }}
                              >
                                {r.type === "in" ? (
                                  <ArrowDown size={10} />
                                ) : (
                                  <ArrowUp size={10} />
                                )}
                                {r.type === "in" ? "Masuk" : "Keluar"}
                              </Badge>
                            </td>
                            <td
                              className={`text-center fw-semibold ${r.type === "in" ? "text-success" : "text-danger"}`}
                            >
                              {r.type === "in" ? "+" : "-"}
                              {r.quantity}
                            </td>
                            <td>{r.reference_type}</td>
                            <td>{r.notes}</td>
                            <td>{r.user_name}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                page={stockPage}
                totalCount={stockTotal}
                pageSize={PAGE_SIZE}
                onPageChange={handleStockPageChange}
                label="pergerakan stok"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
